import CourseSettingsService from 'services/course-settings-service';
import LocaleService from 'services/locale-service';
import utils from 'lib/utils';
/*
 * CustomPageService provides a mechanism for injecting or replacing pages via a customization.
 *
 * The customization response can replace pages by providing a response like this:
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "some-page": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page"
 *         }
 *       }
 *     }
 *   }
 *
 * The customization response can inject pages before or after a certain page key like this:
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "injected-before": {
 *             "url": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page",
 *             "before": "some-page-key"
 *           },
 *           "injected-after": {
 *             "url": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page",
 *             "after": "some-page-key"
 *           }
 *         }
 *       }
 *     }
 *   }
 *
 * The customization response can pull in a set of blocks to a page like this:
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "some-page": {
 *             "some-slug": "https://everfi.github.io/course-customizations/customizations/Skeletor/example-page"
 *           }
 *         }
 *       }
 *     }
 *   }
 *
 *  Then `some-page` and only `some-page` can use the custom-page block like this
 *    - type: custom-page
 *      slug: some-slug
 *  Note that the page could use other blocks in conjunction with this block, including another custom-page block
 *
 * The customization response can also skip pages with a null in the customizations_map
 *   {
 *     "type": "page-content-customization",
 *     "customizations_map": {
 *       "some-module": {
 *         "some-activity": {
 *           "some-skipped-page": null
 *         }
 *       }
 *     }
 *   }
 *
 *  The URL in those examples is the base url for a file called metadata.json which describes the other files to fetch
 *    {
 *      "locales": [ "en", "es" ],
 *      "format": "yaml"
 *    }
 *  Will fetch content.yml, locales/en.json, and locales/es.json
 *
 *
 *  Note About Loading page-n.js Files
 *  ==================================
 *
 *  Custom pages currently do not include the page-n.js script file. To include these, they can be pushed into
 *  the pageQueue like all of the other resources for each page. Then call `response.text()` and eval it
 *  with the common js wrapper inline. It'll look something like this for the current namespace:
 *  ```
 *  fetch(`url-to-script-file`).then(r => r.text()).then(scriptBody => {
*     eval(`
 *      window.require.register('modules/${EF.module.currentActivity.currentPage.get('moduleKey')}/${EF.module.currentActivity.currentPage.get('activityKey')}/${EF.module.currentActivity.currentPage.get('key')}/${EF.module.currentActivity.currentPage.get('key')}', function(exports, require, module) {
 *      ${scriptBody}
 *    });
 *  `);
 *  });
 *  ```
 *  eval is very dangerous, if this is implemented, make sure the script comes from a trusted source
 *  the risk for XSS is very high here since this more or less intentionally injects a raw
 *  script file on the fly. Double, triple check the security of such a feature before moving forward with it
 */
export default class CustomPageService {
  /*
   * Loads a single page on the fly after everything is initialized.
   * The localizations are added to the current LocaleService namespace.
   * Used by app/views/block/custom-page/custom-page-view.js
   *
   * @returns {Promise} Promise object where the result has the blocks for the page and the locales
   * @example
   * CustomPageService.loadPage('some-slug').then(({blocks, locales}) => {
   *   // blocks is an array of renderable blocks
   *   // locales is an object like this:
   *   //  { en: { someKey: 'some translation', es: { someKey: 'Spanish translation' } }}
   * });
   */
  static loadPage(slug, module, activity, page) {
    if(CustomPageService.hasPageCustomization(slug, module, activity, page)) {
      const url = CustomPageService.customizationUrl(slug, module, activity, page);
      return fetch(`${url}/metadata.json`)
        .then(r => r.json())
        .then(metadata => {
          const queue = [];

          if(['yaml', 'yml', 'json'].includes(metadata.format)) {
            metadata.format = metadata.format === 'yaml' ? 'yml' : metadata.format;
            queue.push(fetch(`${url}/content.${metadata.format}`).then(r => {
              return r.text().then(data => {
                return Promise.resolve(
                  metadata.format === 'json' ? JSON.parse(data) : YAML.parse(data)
                );
              });
            }));
          }

          if(metadata.locales && Array.isArray(metadata.locales)) {
            queue.push(...metadata.locales.map(locale => {
              return fetch(`${url}/locales/${locale}.json`).then(r => r.json()).then(data => {
                return Promise.resolve({ [locale]: data });
              });
            }));
          }

          return Promise.all(queue).then(([page, ...locales]) => {
            return { blocks: page.blocks, locales: locales.reduce((result, locale) => {
              Object.assign(result, locale);
              return result;
            }, {})};
          });
        });
    } else {
      return CustomPageService.emptyValue();
    }
  }

  static hasPageCustomization(slug, module, activity, page) {
    if(!slug) { return false; }
    return CustomPageService.customizationUrl(slug, module, activity, page) !== null;
  }

  static customizationUrl(slug, module, activity, page) {
    const customization = EF.customizations.findByType('page-content-customization');
    if(customization && customization.has('customizations_map')) {
      const map = customization.get('customizations_map');
      const url = [ module, activity, page, slug ].reduce((result, key) => result !== null && result.hasOwnProperty(key) ? result[key] : null, map);
      return url !== null ? url.replace(/{{courseUrl}}/, CourseSettingsService.get('courseUrl')) : null;
    }
    return null;
  }

  static modulePaths() {
    return {
      module: EF.module.get('key'),
      activity: EF.module.currentActivity.get('key'),
      page: EF.module.currentActivity.currentPage.get('key')
    };
  }

  static emptyValue() {
    console.warn('CustomPageService: could not resolve a customization. Check that the slug is correct and the customization exists'); //eslint-disable-line no-console
    return new Promise(resolve => resolve({blocks: [], locales: []}));
  }

  /*
   * This is responsible for injecting the page metadata into the module structure object before initialization
   *
   * @param {Array} modules - Raw serialized json from before initialization. This will be mutated in place.
   * @param {Object} info - Contains before and after page keys to figure out where to inject the page
   */
  static injectPageMetaData(modules, info, moduleKey, activityKey, pageKey, pageTitle) {
    const module = modules.find(m => m.key === moduleKey);
    if(module) {
      const activity = module.activities.find(a => a.key === activityKey);
      if(activity && activity.pages && Array.isArray(activity.pages)) {
        // sort pages by the index property
        activity.pages.sort((page1, page2) => page1.index < page2.index ? -1 : 1);

        var injectedPage = {
          title: pageTitle,
          key: pageKey,
          script: false,
          custom: 'inject' // one way of marking injected pages
        };

        // inject the page before or after the page key
        var beforeIndex = activity.pages.findIndex(page => page.key === info.before);
        var afterIndex = activity.pages.findIndex(page => page.key === info.after);
        if(beforeIndex >= 0) {
          activity.pages.splice(beforeIndex, 0, injectedPage);
        } else if(afterIndex >= 0) {
          injectedPage.index = afterIndex + 1;
          activity.pages.splice(afterIndex + 1, 0, injectedPage);
        }

        // since it was sorted by index and then the pages were injected, this normalizes all of the
        // page indices and makes them sequential
        activity.pages.forEach((page, index) => {
          page.index = index;
        });
      }
    }
  }

  /*
   * Attaches follower pages to pages that happen to be next to a page that has been skipped via a customization.
   *
   * When the progress for a page is saved in page-view.js, it will save the progress for all of its followers as well
   * This is needed because the progress can get out of sync if a page is skipped late in a module.
   * Saving that progress early would put the current page pointer at that later page, when a lot of the earlier pages haven't been completed yet
   */
  static attachFollowers(modules, moduleContent, page, pageIndex, options) {
    const { moduleKey, activityKey, pageKey } = options;
    // compile an array of all the pages in all of the modules and all of the activities
    // when attaching a follower, it can pick the next page without having to traverse through
    // modules and activities to find the next appropriate page
    const pagesByIndex = modules.reduce((result, module) => {
      if(Array.isArray(module.activities)) {
        // sort all of the activities and pages by the index property, so that later the
        // next page index can be determined even if the index is misordered
        module.activities.sort((activity, comparable) => activity.index < comparable.index ? -1 : 1);
        module.activities.forEach(activity => {
          if(Array.isArray(activity.pages)) {
            activity.pages = CustomPageService.markLastPageOfModuleActivity(activity.pages );
            result = result.concat(activity.pages);
          }
        });
      }
      return result;
    }, []);

    const follower = { activityKey, pageKey, index: page.index };

    // find the actual page from the customization and attach a follower to the next page
    const module = modules.find(m => m.key === moduleKey);
    if(module && Array.isArray(module.activities)) {
      const activity = module.activities.find(a => a.key === activityKey);
      if(activity && Array.isArray(activity.pages)) {
        const page = activity.pages.find(p => p.key === pageKey);
        const index = pagesByIndex.findIndex(p => p === page);
        const pageToAttachFollowersTo = page.lastPageOfModule && pagesByIndex[index - 1] ? pagesByIndex[index - 1] : pagesByIndex[index + 1];
        if(pageToAttachFollowersTo) {
          pageToAttachFollowersTo.followers = Array.isArray(pageToAttachFollowersTo.followers) ?
                                                    [...pageToAttachFollowersTo.followers, follower] : [ follower ];
        } else {
          console.error('CustomPageService: Could not handle progress for a page that either does not exist or is the last page in the last activity of the last module. It is very likely progress will not work correctly.');  //eslint-disable-line no-console
        }
      }
    }
  }

  /*
   * This is responsible for loading n pages and normalizing the data so that it can be merged into
   * the raw JSON before initialization
   *
   * @param {Array}  customizations_map - Array of all of the customizations with their urls
   * @param {Object} modules      - The module structure object from before course initialization.
   *                                This will be mutated in place to add the injected and replaced pages
   */
  static loadPages(customizations_map, modules) {
    // The main queue for each page, each promise will resolve when the page is fully loaded
    const queue = [];
    Object.entries(customizations_map).forEach(([moduleKey, activities]) => {
      Object.entries(activities).forEach(([activityKey, pages]) => {
        Object.entries(pages).forEach(([pageKey, info]) => {
          if(info === null || info === undefined || info === '') {
            // null, undefined, or empty string means the page is to be skipped, it will be removed before init
            queue.push(Promise.all([
              new Promise((resolve) => {
                return resolve((modulesRef, moduleContentRef) => {
                  // modulesRef and moduleContentRef are the references to the final module and module content objects
                  // they are here so that they can be mutated in place, in this case we found a null replacement
                  // which needs to be removed
                  const moduleRef = modules.find(m => m.key === moduleKey);
                  if(moduleRef && Array.isArray(moduleRef.activities)) {
                    const activityRef = moduleRef.activities.find(a => a.key === activityKey);
                    if(activityRef && activityRef.pages) {
                      // these should be sorted already, but make sure
                      // because the index of the followers will not work otherwise
                      activityRef.pages.sort((page1, page2) => page1.index < page2.index ? -1 : 1);
                      const pageIndex = activityRef.pages.findIndex(p => p.key === pageKey);
                      const page = activityRef.pages[pageIndex];
                      CustomPageService.attachFollowers(modulesRef, moduleContentRef, page, pageIndex, {
                        moduleKey, activityKey, pageKey
                      });

                      activityRef.pages.splice(pageIndex, 1);
                      delete moduleContentRef[moduleKey][activityKey][pageKey];
                    }
                  }
                });
              })
            ]));
          } else if(Object.keys(info).includes('url')) {
            const url =  typeof info === 'string' ? info : info.url;
            queue.push(fetch(`${url}/metadata.json`).then(response => response.json()).then(metadata => {
              // a sub queue for all of the files for each page, each promise will resolve when
              // each part is loaded like content.yml and each of the locale files
              const pageQueue = [];
              if(['yaml', 'yml', 'json'].includes(metadata.format)) {
                metadata.format = metadata.format === 'yaml' ? 'yml' : metadata.format;
                pageQueue.push(
                  fetch(`${url}/content.${metadata.format}`).then(r => r.text()).then(data => {
                    const content = metadata.format === 'json' ? JSON.parse(data) : YAML.parse(data);
                    Object.assign(content, { custom: info.after || info.before ? true : 'replace' });
                    const mergable = {
                      [moduleKey]: {
                        [activityKey]: {
                          [pageKey]: content
                        }
                      }
                    };
                    if(info.after || info.before) {
                      CustomPageService.injectPageMetaData(modules, info, moduleKey, activityKey, pageKey, metadata.title || '');
                      return Promise.resolve(mergable);
                    } else {
                      //this is a page replacement in the module
                      return new Promise(resolve => {
                        resolve((modules, moduleContentRef) => {
                          CustomPageService.replacePage(
                            moduleContentRef,
                            mergable,
                            moduleKey,
                            activityKey,
                            pageKey,
                            metadata.title || ''
                          )
                          return mergable;
                        });
                      });
                    }

                  })
                );
              }
              if(metadata.locales && Array.isArray(metadata.locales)) {
                pageQueue.push(...metadata.locales.map(locale => {
                  return fetch(`${url}/locales/${locale}.json`).then(r => r.json()).then(data => {
                    return Promise.resolve({ [locale]: data });
                  });
                }));
              }
              return Promise.all(pageQueue);
            }));
          }
        });
      });
    });
    return Promise.all(queue);
  }

  /*
   * This is the entry point for injecting pages before initialization.
   * Note: it mutates the parameters
   *
   * @param {Object} moduleContent - the raw serialized json for the module including all
   *                                 of the blocks for each page
   * @param {Object} modules -  the raw serialized json containing only the structure of
   *                            the module without the blocks for each page
   */
  static replaceCustomPages(modules, moduleContent, groupsJSON) {
    const customizations = EF.customizations.findByType('page-content-customization');
    if(customizations && customizations.has('customizations_map')) {
      const customizations_map = customizations.get('customizations_map');
      return CustomPageService.loadPages(customizations_map, modules).then((bundles) => {
        bundles.forEach(([mergable, ...locales]) => {
          // mergable is a full object with the module, activity, and page keys like this:
          // {
          //   someModule: { someActivity: { somePage: { blocks: [ ... ] } } }
          // }
          // It may also be a function, which means a null replacement was found
          if(typeof mergable === 'function') {
            // found a callback which means the page has been removed or replaced, call it
            // and let the callback mutate modules and moduleContent to remove the page or replace the blocks
            // if it's a replacement, will also return the mergable page data so locales are added correctly below
            mergable = mergable(modules, moduleContent);
          } else if(mergable) {
            _.merge(moduleContent, mergable);
          }

          // traverse down to each page and inject the locales with the right namespace
          if(mergable && locales && Array.isArray(locales)) {
            Object.entries(mergable).forEach(([moduleKey, activities]) => {
              Object.entries(activities).forEach(([activityKey, pages]) => {
                Object.entries(pages).forEach(([pageKey]) => {
                  locales.forEach(locale => {
                    if(locale) {
                      Object.entries(locale).forEach(([code, data]) => {
                        LocaleService.addResource(
                          code, `${moduleKey}.${activityKey}.${pageKey}`, {
                            [moduleKey]: {
                              [activityKey]: {
                                [pageKey]: data
                              }
                            }
                          }
                        );
                      });
                    }
                  });
                });
              });
            });
          }
        });
        return Promise.resolve([ modules, moduleContent, groupsJSON ]);
      });
    } else {
      return new Promise((resolve) => resolve([ modules, moduleContent, groupsJSON ]));
    }
  }

  static markLastPageOfModuleActivity(pages) {
    const orderedPages = CustomPageService.orderPagesByIndex(pages);
    if(orderedPages.length > 0) {
      utils.getLastArrayElement(orderedPages).lastPageOfModule = true;
    }
    return orderedPages;
  }
  static replacePage(moduleContentRef, mergable, moduleKey, activityKey, pageKey) {
    if(moduleContentRef[moduleKey][activityKey][pageKey]) {
      moduleContentRef[moduleKey][activityKey][pageKey] = mergable[moduleKey][activityKey][pageKey];
    }
  }

  static orderPagesByIndex(pages) {
    return pages.filter(p => p !== undefined).sort((currentPage, nextPage) => currentPage.index < nextPage.index ? -1 : 1);
  }
}
